import type { ComponentProps } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import * as Dialog from '@radix-ui/react-dialog';
import { CloseButton } from '@/components/ui/close-button';
import { cn } from '@/lib/utils';

import type { useModal } from './use-modal';

interface ModalProps extends ReturnType<typeof useModal>, ComponentProps<'div'> {
  dismissDisabled?: boolean;
  dismissOnOutsideClick?: boolean;
  overlayWrapperClassname?: string;
  title?: string;
  type?: 'simple' | 'default' | 'custom';
}

export function Modal({
  children,
  className,
  dismissDisabled = false,
  dismissOnOutsideClick = true,
  dismiss,
  isOpen,
  overlayWrapperClassname,
  title,
  type = 'default',
}: ModalProps) {
  const { t } = useTranslation();
  const id = useId();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (dismissDisabled) return;
      dismiss();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-gray-500/20',
            overlayWrapperClassname
          )}
        >
          <Dialog.Content
            className={cn(
              'relative w-full max-w-lg rounded-[20px] bg-primary-lighter p-4 shadow-[0_10px_20px_-6px_rgba(0,0,0,0.20)] sm:p-5',
              className
            )}
            onEscapeKeyDown={(e) => {
              if (dismissDisabled) {
                e.preventDefault();
              }
            }}
            onPointerDownOutside={(e) => {
              if (dismissDisabled || !dismissOnOutsideClick) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              if (dismissDisabled || !dismissOnOutsideClick) {
                e.preventDefault();
              }
            }}
          >
            {type === 'custom' ? (
              <Dialog.Title className="sr-only" id={id}>
                {title}
              </Dialog.Title>
            ) : type === 'default' ? (
              <>
                <Dialog.Title
                  className="min-w-0 break-words px-2 pr-10 pt-0.5 text-xl font-semibold leading-7 text-text-primary sm:px-3 sm:pr-10"
                  id={id}
                >
                  {title}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <CloseButton
                    aria-label={t('close')}
                    className="absolute right-3 top-3 size-8 [&_svg]:size-5"
                  />
                </Dialog.Close>
              </>
            ) : (
              <Dialog.Title
                className="px-2 text-xl font-semibold leading-7 text-text-primary sm:px-3"
                id={id}
              >
                {title}
              </Dialog.Title>
            )}
            {children}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type ModalBodyProps = React.ComponentPropsWithRef<'div'>;

export function ModalBody({ className, ...props }: ModalBodyProps) {
  const classes = cn(
    'bg-primary-lighter px-2 py-2.5 text-base text-text-secondary sm:p-3',
    className
  );

  return <div className={classes} {...props} />;
}

Modal.Body = ModalBody;

type ModalActionsProps = React.ComponentPropsWithRef<'div'>;

export function ModalActions({ className, ...props }: ModalActionsProps) {
  const classes = cn(
    'flex flex-col gap-3 px-2 pt-4 sm:flex-row-reverse sm:justify-start sm:px-3',
    className
  );

  return <div className={classes} {...props} />;
}

Modal.Actions = ModalActions;
